/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2016-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
*/

angular.module('bulbApp.pageModule').service('BulbPageService', [
  '$location', '$state', '$q', '$rootScope', 'Restangular', 'BulbAuthService', 'BulbAjaxService',
  'BulbBusyService', 'BulbModalService', 'BulbUnitService', 'BulbErrorService', 'SerialSaveService',
  'UnitStateStore', 'AUTH',
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  BulbPageService,
]);

function BulbPageService(
  $location,
  $state,
  $q,
  $rootScope,
  Restangular,
  BulbAuthService,
  BulbAjaxService,
  BulbBusyService,
  BulbModalService,
  BulbUnitService,
  BulbErrorService,
  SerialSaveService,
  UnitStateStore,
  AUTH,
) {
  const that = this;
  that.currentUser = BulbAuthService.currentUser;
  that.currentUserPrefs = that.currentUser.prefs;
  // For tying the events in the pageviewheader-controller to the publishpanel-controller when enhancing navigation
  // away from an unpublished page.
  that.userClickedBackArrow = false;

  // Again, for tying events from the pageview-controller to the pageviewheader-controller when navigating
  // to new states.
  that.pendingStateChangeOnPublish = null;

  /**
   * Default page values.
   *
   * @type {{mode: string, canEdit: boolean, editing: boolean}}
   */
  const defaultPage = {
    canEdit: false,
    mode: 'default',
    editing: false,
    // questions: {}
  };

  this.page = {};

  this.compositeUnit = {};

  /**
   * Function that updates the current pages values and details.
   */
  function updatePageDetails() {
    if (BulbUnitService.currentUnit.unitType === 'DraftPageUnit'
      || BulbUnitService.currentUnit.unitType === 'PublishedPageUnit'
      || BulbUnitService.currentUnit.unitType === 'ArchivePageUnit') {
      angular.copy(defaultPage, that.page);
      that.page.unit = BulbUnitService.currentUnit;
      that.page.canEdit = BulbAuthService.currentUser.hasPermission(that.page.unit, AUTH.PERMISSIONS.UPDATE) && !that.page.unit.viewOnly;

      // update the share status for the page
      const ShareStatus = UnitStateStore.getShareStatusEnum();
      if (that.page.unit.isPublished) {
        if (that.page.unit.isModified) {
          UnitStateStore.setShareStatus(ShareStatus.MODIFIED);
        } else {
          UnitStateStore.setShareStatus(ShareStatus.SHARED);
        }
      } else {
        UnitStateStore.setShareStatus(ShareStatus.PRIVATE);
      }
    } else if (BulbUnitService.currentUnit.unitType === 'CompositeUnit') {
      that.compositeUnit.unit = BulbUnitService.currentUnit;
    } else {
      angular.copy({}, that.page);
    }
  }

  /**
   * Updates the page after we replace the embed block with attached file block.
   */
  this.updatePageAfterReplaceEmbed = function updatePageAfterReplaceEmbed() {
    updatePageDetails();
    $rootScope.$broadcast('update-page-details');
  };

  /**
   * Updates the current page details after every autosave
   */
  this.updatePageAfterAutoSave = function updatePageAfterAutoSave() {
    updatePageDetails();
    $rootScope.$broadcast('update-page-details');
  };

  /**
   * Initial page update call.
   */
  updatePageDetails();

  // Watch for when the currentUnit changes, and update appropriate page values for the new unit;
  $rootScope.$watch(() => BulbUnitService.currentUnit.id, () => {
    // TODO CLEAN : this broadcast is here because sometimes this is getting called when refreshing a page after
    // the page view controller is already loaded, and it is resetting the editing value to false.  This
    // broadcast allows the controller to update the editing value.  Probably a cleaner way of handling this.
    updatePageDetails();
    $rootScope.$broadcast('update-page-details');
  });

  /**
   * Create a new page as a child of the author page.
   *
   * DO LATER : a virtually identical method exists in the composite view controller.  Probably want to unify them.
   */
  this.createPage = function createPage(parentUnitId) {
    BulbBusyService.showBusyDuring(
      BulbAjaxService.createPage(parentUnitId).then((response) => {
        if (response.success) {
          $state.go('unit', { unitPath: response.path });
        } else {
          BulbErrorService.error(`Cannot create page: ${response.message}`);
        }
      }, (error) => {
        BulbErrorService.error('Cannot create page', error);
      }),
    );
  };

  /**
   * Method for publishing the page.
   * @param page Contains data of page.
   */
  this.publish = function publish(page) {
    $rootScope.$broadcast('navigationAwayApproval', true);

    SerialSaveService.save({
      showBusy: true,
      save: () => Restangular.one('units', page.unit.id)
        .customPOST({}, 'publish')
        .then((result) => {
          // Uses the translation code to check for this specific spam control case
          const flaggedMessage = 'server_error_body_code';
          if (!result.success && result.message === flaggedMessage) {
            BulbModalService.show({
              titleCode: 'server_error_title',
              bodyCode: flaggedMessage,
              showCancelButton: false,
            });
            return;
          }

          // Update share status to reflect that the page has been published:
          const ShareTransition = UnitStateStore.getShareTransitionEnum();
          UnitStateStore.transitionShareStatus(ShareTransition.SHARE);

          if (!page.unit.isPublished) {
            $rootScope.$broadcast('togglePanel');
          }
          if (page.unit.path === result.path) {
            // Unit path didn't change, so force a state refresh
            $state.reload();
          } else {
            // Unit path did change, so a simple location change will work
            $location.path(`/u/${result.path}`).replace();
          }

          // If the user commanded another state to navigate to, lets navigate there!
          if (that.pendingStateChangeOnPublish) {
            // User commanded a new state, so take them there after publish.
            $state.go(
              that.pendingStateChangeOnPublish.toState,
              that.pendingStateChangeOnPublish.toParams,
            );
            // Set the pendingStateChangeOnPublish object to null
            that.pendingStateChangeOnPublish = null;
          }
        }),
    });
  };

  this.publishWithoutReload = function publishWithoutReload(page) {
    return SerialSaveService.save({
      save: () => Restangular.one('units', page.unit.id).customPOST({}, 'publish').then((result) => {
        // Uses the translation code to check for this specific spam control case
        const flaggedMessage = 'server_error_body_code';
        if (!result.success && result.message === flaggedMessage) {
          BulbModalService.show({
            titleCode: 'server_error_title',
            bodyCode: flaggedMessage,
            showCancelButton: false,
          });
        }

        // Update share status to reflect that the page has been edited:
        const ShareTransition = UnitStateStore.getShareTransitionEnum();
        UnitStateStore.transitionShareStatus(ShareTransition.SHARE);
      }),
    });
  };

  /**
   * Add margin to list elements to create indentation effect.
   */
  this.addMarginToListForMediumEditor = function () {
    setTimeout(() => {
      const pageContentDiv = document.querySelector('div.bulb-page-content');
      if (pageContentDiv) {
        Array.from(['ul', 'ol']).forEach((listEle) => {
          Array.from(pageContentDiv.getElementsByTagName(listEle)).forEach((ele) => {
            ele.classList.add('list-margin');
            // 2 rem indentation between normal text and list elements.
            ele.setAttribute('style', `margin-left:${ele.offsetLeft + 32}px;`);
            ele.classList.remove('list-margin');
          });
        });
      }
    });
  };
}
